/**
 * The preferences blob a tenant is left with after they clear their sample data.
 *
 * Kept in its own module, free of redux and API imports, because it is the one piece of the
 * sample-delete flow with real logic in it and it needs to be testable on its own.
 *
 * WHY IT DROPS THINGS RATHER THAN MERGING THEM
 * --------------------------------------------
 * Clearing sample data starts a NEW onboarding run. `OnboardingWizard` is not only a resume
 * cache: node-api derives the whole onboarding funnel from it on every preferences write —
 * `saved` from the data buckets, `reached` from the progress pointer — and records each
 * (tenant, sub-step) once, first write winning forever.
 *
 * This reset is PUT *after* `data_clear/sample_data_clear` has already been recorded, so any
 * sample-run data still in the blob is re-emitted as brand-new funnel rows stamped on the far
 * side of the run boundary. The analytics side filters sub-step rows to "created after the
 * tenant's last sample clear" for precisely this case and it cannot help — the replayed rows
 * genuinely are newer than the boundary. A read-side filter can never suppress a write-side
 * replay; the write has to stop.
 *
 * Symptom before this: a school that cleared its sample data landed back on the wizard with
 * Branches already ticked and counted, having entered nothing (kinschool1, bug 315).
 */

/**
 * The `OnboardingWizard` keys holding the PREVIOUS run's work — entered data, resume pointer,
 * chosen mode. All of them go.
 *
 * `tutorial` is deliberately absent: whether the user has seen the dashboard tour is a property
 * of the person, not of the run. `sampleData` is absent too — it is reset explicitly below
 * rather than dropped, so the wizard can tell "cleared" from "never had any".
 */
export const PREVIOUS_RUN_WIZARD_KEYS = [
  'setup',
  'curriculum',
  'classesDraft',
  'progress',
  'mode',
  // "Save and finish later": where the previous run paused, and when (stamped by node-api's
  // `onboarding/save-progress`). Left behind, the new run would resume into the sample run's
  // position, or read as a returning user whose resume point was lost.
  'resumeAt',
  'savedAt',
] as const

export const buildPostSampleDeletePreferences = (
  stored: Record<string, unknown> = {}
): Record<string, unknown> => {
  // A stored value that no longer parses comes back as its raw text; spreading that string
  // would write one key per character.
  const existing =
    stored !== null && typeof stored === 'object' && !Array.isArray(stored) ? stored : {}
  const wizard = (existing.OnboardingWizard ?? {}) as Record<string, unknown>

  const carried = {...wizard}
  PREVIOUS_RUN_WIZARD_KEYS.forEach((key) => delete carried[key])

  return {
    ...existing,

    OnboardingWizard: {
      ...carried,

      tutorial: {
        ...(wizard.tutorial as Record<string, unknown> | undefined),

        dashboard: true,
      },

      sampleData: {
        active: false,

        levelId: undefined,

        durationId: undefined,

        branchId: undefined,
      },
    },
  }
}
